import React from "react";
import styled from "styled-components";
import PerformanceValue from "../../../performance/PerformanceValue";
import {RankingRow} from "./RankingRow";
import {RankCell, UnrankedCell} from "./RankColumn";
import {NameCell} from "./NameColumn";
import {ColumnNameHeadings} from "../../Sections";
import {VirtualizedTable} from "../../../elements/VirtualizedTable";
import {Column} from "react-virtualized";
import {WarningContainer, WarningIcon} from "../../homepage/allPortfolios/WarningIcon";
import {sortBasedOnState} from "../../../sorting/sort";
import {RightAlignedSortableCell, SortableHeading} from "../../../sorting/SortableHeading";
import {kpiBriefDescription, performanceValueSlimDescription} from "../../../performance/Kpi";
import {ValueCell, ValueColumn} from "./ValueColumn";

const CallToActionContainer = styled.div`
    display: flex;
    height: 28px;
    font-size: 12px;
    color: white;
    padding-left: 30px;
    align-items: center;
`;

const CallToActionNotification = styled(CallToActionContainer)`
    background-color: #bd2d2d;
`;

const CallToActionInfo = styled(CallToActionContainer)`
    background-color: #9b9b9b;
`;

const RankingHeader = styled.div`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    font-size: 11px;
`;

const CallToActionText = ({actionToGetRanked}) => {
    if (!actionToGetRanked) {
        return null;
    }
    const {eligibility, other} = actionToGetRanked;

    return eligibility
        ? <CallToActionNotification className="t-unrank-call-to-action-message">
            <div>{eligibility}</div>
        </CallToActionNotification>
        : <CallToActionInfo className="t-unrank-call-to-action-message">
            <div>{other}</div>
        </CallToActionInfo>;
};

const EmptyPadding = styled.div`
    flex-shrink: 0;
    width: 20px;
`;

const WarningWrapper = styled(WarningContainer)`
    flex-shrink: 0;
    margin-left: 8px; 
    margin-right: 9px;   
`;

const SelectedRankMarker = styled.div`
    margin-right: 14px;
    border-left: 16px solid #3b7433;
    border-top: 28px solid transparent;
    border-bottom: 28px solid transparent;
`;

const KpiHeaderPill = styled.span`
    display: inline-block;
    background-color: #458535;
    color: white;
    border-radius: 3px;
    padding: 1px 4px;
    font-size: 70%;
    margin-left: 6px;
    vertical-align: 10%;
`;

function columnsRelevantTo(kpi, shareBased) {
    switch (kpi) {
        case "customScore":
            return ["relativeHitRate", "normalisedReturn", "consistency", "intensity"];

        case "relativeReturnPercent":
            return shareBased ? ["relativePnl", "relativeHitRate"] : ["relativeHitRate"];
        case "relativeHitRate":
            return [shareBased ? "relativePnl" : "relativeReturnPercent"];
        case "relativePnl":
            return ["relativeReturnPercent", "relativeHitRate"];

        case "absoluteReturnPercent":
            return shareBased ? ["absolutePnl", "absoluteHitRate"] : ["absoluteHitRate"];
        case "absoluteHitRate":
            return [shareBased ? "absolutePnl" : "absoluteReturnPercent"];
        case "absolutePnl":
            return ["absoluteReturnPercent", "absoluteHitRate"];

        default:
            return [];
    }
}


const RankingTable = ({rankingList,
                       selectedAuthorId,
                       signedOnUserId,
                       sort,
                       kpi,
                       benchmarkSource,
                       defaultBenchmark,
                       currency,
                       shareBased,
                       actionToGetRanked,
                       sortRankingsFn,
                       onRowClick,
                       scrollToAuthor}) => {

    if (!rankingList || rankingList.length === 0) {
        return null;
    }

    const RankingHeading = (props) => (
        <SortableHeading {...props}
                         sortState={sort}
                         tableName="portfolio_rankings"
                         sortByFieldFn={sortRankingsFn}/>
    );

    const transformedList = rankingList.map(r => {
        const v = r.values;
        v.rank = r.rank;
        v.name = r.name;
        v.authorId = r.authorId;
        return v;
    });
    const sortedList = sortBasedOnState(transformedList, sort);

    const selectedIndex = sortedList.findIndex(ranking => ranking.authorId === selectedAuthorId);
    const scrollToIndex = !sort.direction && selectedIndex > 0 ? selectedIndex : 0;
    const showActionToGetRanked = selectedAuthorId === signedOnUserId;
    const eligibilityViolation = actionToGetRanked && actionToGetRanked.eligibility;
    const columns = [];

    columns.push(<Column width={20}
                         style={{marginLeft: "0px", overflow: "visible"}}
                         headerStyle={{marginLeft: "0px"}}
                         dataKey="authorId"
                         horizontalAlignment="flex-end"
                         headerRenderer={() => null}
                         cellRenderer={({cellData}) => {
                             const isSelectedRow = selectedAuthorId === cellData;
                             const yourRow = signedOnUserId === cellData;
                             if (!isSelectedRow || !yourRow) {
                                 return <EmptyPadding/>;
                             }
                             if (yourRow && eligibilityViolation) {
                                 return <WarningWrapper>
                                     <WarningIcon className="t-warning-unranked-icon">!</WarningIcon>
                                 </WarningWrapper>;
                             }
                             return <SelectedRankMarker/>;
                         }}/>);
    columns.push(<Column width={50}
                         dataKey="rank"
                         horizontalAlignment="flex-end"
                         headerRenderer={() => <RankingHeading descendingFirst={false} fieldName="rank">Rank</RankingHeading>}
                         cellRenderer={({cellData}) => cellData ? <RankCell className="t-rank">{cellData}</RankCell>
                                                                : <UnrankedCell className="t-rank">Unranked</UnrankedCell>}/>);
    const relevantColumns = columnsRelevantTo(kpi, shareBased);
    columns.push(<Column width={relevantColumns.length < 3 ? 200 : 130}
                         dataKey="name"
                         headerRenderer={() => <RankingHeading descendingFirst={false} fieldName="name">Name</RankingHeading>}
                         cellRenderer={({cellData}) => <NameCell className="t-name">{cellData}</NameCell>}/>);

    const ideaCounts = [];
    if (relevantColumns.length < 3) {
        ideaCounts.push({name: "numberOfIdeas", heading: "Total Ideas"});
        ideaCounts.push({name: "numberOfNewIdeas", heading: "New Ideas"});
    }
    ideaCounts.map(field => columns.push(<Column width={70}
                                                 style={{overflow: "visible"}}
                                                 dataKey={field.name}
                                                 headerRenderer={() => <ValueColumn>
                                                     <RankingHeading horizontalAlignment="flex-end" fieldName={field.name}>
                                                         {field.heading}
                                                     </RankingHeading>
                                                 </ValueColumn>}
                                                 cellRenderer={({cellData}) => <ValueCell className={"t-" + field.name}>
                                                     <RightAlignedSortableCell>
                                                         {cellData}
                                                     </RightAlignedSortableCell>
                                                 </ValueCell>}/>));


    columns.push(<Column width={relevantColumns.length < 3 ? 120 : 80}
                         dataKey={kpi}
                         headerRenderer={() => <ValueColumn className="t-kpi-heading">
                             <RankingHeading fieldName={kpi}>
                                 {kpiBriefDescription(kpi, currency, benchmarkSource, defaultBenchmark)}
                                 <KpiHeaderPill>KPI</KpiHeaderPill>
                             </RankingHeading>
                         </ValueColumn>}
                         cellRenderer={({cellData}) => <ValueCell className="t-kpi-value">
                             <RightAlignedSortableCell>
                                 <PerformanceValue name={kpi} value={cellData}/>
                             </RightAlignedSortableCell>
                         </ValueCell>}/>);


    relevantColumns.map(r => columns.push(<Column width={r.length > 10 ? 90: 70}
                                                  style={{overflow: "visible"}}
                                                  dataKey={r}
                                                  headerRenderer={() => <ValueColumn>
                                                      <RankingHeading horizontalAlignment="flex-end" fieldName={r}>
                                                          {performanceValueSlimDescription(r) + (r.endsWith("Pnl") ? ` (${currency})` : "")}
                                                      </RankingHeading>
                                                  </ValueColumn>}
                                                  cellRenderer={({cellData}) => <ValueCell className={"t-" + r}>
                                                      <RightAlignedSortableCell>
                                                          <PerformanceValue name={r} value={cellData}/>
                                                      </RightAlignedSortableCell>
                                                  </ValueCell>}/>));

    const headerHeight = showActionToGetRanked && actionToGetRanked ? 60 : 32;
    return <VirtualizedTable headerHeight={headerHeight}
                             rowHeight={32}
                             {...(scrollToAuthor && {scrollToIndex: scrollToIndex})}
                             scrollToAlignment="center"
                             rowCount={sortedList.length}
                             rowGetter={({index}) => sortedList[index]}
                             headerRowRenderer={({columns, style}) => <RankingHeader style={style}>
                                 <ColumnNameHeadings className="t-column-headings">
                                     {columns}
                                 </ColumnNameHeadings>
                                 {showActionToGetRanked ? <CallToActionText actionToGetRanked={actionToGetRanked}/> : null}
                             </RankingHeader>}
                             rowRenderer={({index, style, columns}) => {
                                 const ranking = sortedList[index];
                                 const isSelectedRow = selectedAuthorId === ranking.authorId;
                                 return <RankingRow key={index} isSelectedRow={isSelectedRow} ranking={ranking} style={style}
                                                    onClick={ranking.authorId ? () => onRowClick(ranking.authorId) : null}>
                                     {columns}
                                 </RankingRow>;
                             }}>
        {columns}
    </VirtualizedTable>;
};

export default RankingTable;
